import {
    PHOTO_SOURCE_FINGERPRINT_VERSION,
    photoForensicSourceFingerprint,
    photoSourceIdentity,
} from './photo-forensic-lineage';
import { fingerprintValue } from './quote-snapshot-diff';

type UnknownRecord = Record<string, any>;

export const DOCUMENT_DERIVED_VISUAL_SOURCES = new Set([
    'pdf-embed',
    'pdf-page-fallback',
]);

// One bounded migration exception for the exact production receipt and its
// exact raw-indexed derivation ledger that
// exposed this legacy class. v3 did not hash source/sourcePdf* metadata, so a
// generic partial receipt without a contemporaneous derivation ledger cannot
// safely reclassify unreadable customer photos after the fact.
export const LEGACY_DOCUMENT_VISUAL_HEAL_FINGERPRINTS = new Set([
    'fnv1a32:aa69c92e', // Artarmon a5900bb7-5b33-4b1e-8fba-fc8efb1b2c5d (4/65)
]);
const LEGACY_DOCUMENT_VISUAL_DERIVATION_FINGERPRINTS = new Map([
    ['fnv1a32:aa69c92e', 'fnv1a32:d7bf6d05'],
]);

function record(value: unknown): UnknownRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

function stablePhotoId(value: unknown): string {
    const photo = record(value);
    return String(photo?.id || '').trim();
}

/**
 * PDF XObjects and whole-page recovery renders are auxiliary views of an
 * already-attached document. They are not independently imported customer
 * photos and must never acquire project-photo authority merely because Stage 1
 * rendered them while reading the PDF.
 */
export function isDocumentDerivedVisual(value: unknown): boolean {
    const photo = record(value);
    if (!photo) return false;
    const source = String(photo.source || '').trim().toLowerCase();
    const page = Number(photo.sourcePdfPage);
    return DOCUMENT_DERIVED_VISUAL_SOURCES.has(source)
        && !!String(photo.sourcePdfDocId || '').trim()
        && !!String(photo.sourcePdfName || '').trim()
        && !!String(photo.sourcePdfImageId || '').trim()
        && Number.isInteger(page)
        && page > 0;
}

/** The authoritative site-photo collection used by AI, readiness and stages. */
export function selectRequiredProjectPhotos(value: unknown): UnknownRecord[] {
    const photos = (Array.isArray(value) ? value : []) as UnknownRecord[];
    // Preserve the caller's array identity on the overwhelmingly common path;
    // several orchestration seams use identity to avoid redundant work.
    if (!photos.some(isDocumentDerivedVisual)) return photos;
    return photos.filter(photo => !isDocumentDerivedVisual(photo));
}

function exactObservationMap(receipt: UnknownRecord): Map<string, UnknownRecord> | null {
    const observations = Array.isArray(receipt.observations) ? receipt.observations : [];
    const byId = new Map<string, UnknownRecord>();
    for (const candidate of observations) {
        const observation = record(candidate);
        const id = String(observation?.photo_id || '').trim();
        if (!observation || !id || byId.has(id)) return null;
        byId.set(id, observation);
    }
    return byId;
}

function isExactCount(value: unknown, expected: number): boolean {
    if (value === null || value === undefined || value === '') return false;
    const count = Number(value);
    return Number.isInteger(count) && count === expected;
}

function hasExactPreHealLineage(receipt: UnknownRecord, photos: UnknownRecord[]): boolean {
    const attempt = record(receipt.analysis_attempt);
    const coverage = record(receipt.coverage);
    if (!attempt || !coverage) return false;

    const fullSourceCount = photos.length;
    if (String(receipt.coverage_status || '').trim().toLowerCase() !== 'partial'
        || String(coverage.status || '').trim().toLowerCase() !== 'partial') {
        return false;
    }

    // The persisted v3 Artarmon receipt carries four independent authorities
    // for the complete pre-analysis photo set. A fingerprint match is not
    // enough if any one of those authorities is missing or contradictory.
    // input_photos_count is deliberately NOT in this group: in v3 it records
    // the readable/selected subset (4), not the submitted source set (65).
    if (!isExactCount(coverage.expected, fullSourceCount)
        || !isExactCount(coverage.submitted, fullSourceCount)
        || !isExactCount(receipt.submitted_photos_count, fullSourceCount)
        || !isExactCount(attempt.source_count, fullSourceCount)) {
        return false;
    }
    // Some projected/imported v3 receipts may carry an additional root source
    // count. It is not required by the proven Artarmon schema, but if present
    // it cannot disagree with the full source set.
    if (Object.prototype.hasOwnProperty.call(receipt, 'source_count')
        && !isExactCount(receipt.source_count, fullSourceCount)) {
        return false;
    }

    // This migration is intentionally limited to the stable v3 receipt shape.
    // Earlier versions hashed lifecycle-mutating fields, so accepting them here
    // would turn an unverifiable old receipt into new authority.
    if (Number(receipt.source_fingerprint_version) !== PHOTO_SOURCE_FINGERPRINT_VERSION
        || Number(attempt.source_fingerprint_version) !== PHOTO_SOURCE_FINGERPRINT_VERSION) {
        return false;
    }

    const currentFingerprint = photoForensicSourceFingerprint(photos);
    const rootFingerprint = String(receipt.source_fingerprint || '').trim();
    const attemptFingerprint = String(attempt.source_fingerprint || '').trim();
    if (!rootFingerprint
        || !attemptFingerprint
        || rootFingerprint !== currentFingerprint
        || attemptFingerprint !== currentFingerprint) {
        return false;
    }

    return true;
}

function hasExactLegacyPartialTuple(
    receipt: UnknownRecord,
    requiredPhotoCount: number,
    documentVisualCount: number,
): boolean {
    const coverage = record(receipt.coverage);
    if (!coverage) return false;
    if (!isExactCount(receipt.photos_analysed, requiredPhotoCount)
        || !isExactCount(receipt.input_photos_count, requiredPhotoCount)
        || !isExactCount(receipt.selected_photos_count, requiredPhotoCount)
        || !isExactCount(coverage.selected, requiredPhotoCount)
        || !isExactCount(coverage.analysed, requiredPhotoCount)
        || !isExactCount(receipt.unreadable_photo_count, documentVisualCount)
        || receipt.requires_review !== true) {
        return false;
    }

    const observations = Array.isArray(receipt.observations) ? receipt.observations : [];
    const unreadable = Array.isArray(receipt.unreadable_photos) ? receipt.unreadable_photos : [];
    if (observations.length !== requiredPhotoCount || unreadable.length !== documentVisualCount) {
        return false;
    }

    const fullSourceCount = requiredPhotoCount + documentVisualCount;
    const expectedError = `Photo forensic coverage is incomplete (${requiredPhotoCount}/${fullSourceCount} project photos). Retry photo analysis before continuing.`;
    // Only the deterministic coverage wrapper is safe to supersede. Provider,
    // persistence and unknown root errors must survive as hard failures.
    return typeof receipt.error === 'string' && receipt.error === expectedError;
}

export function documentEvidenceExclusionReceipt(
    photos: UnknownRecord[],
    rawPhotosValue: unknown = photos,
): UnknownRecord[] {
    const rawPhotos = (Array.isArray(rawPhotosValue) ? rawPhotosValue : photos) as UnknownRecord[];
    return photos.map((photo, subsetIndex) => {
        const rawIndex = rawPhotos.indexOf(photo);
        const sourceIndex = rawIndex >= 0 ? rawIndex : subsetIndex;
        return {
        ...photoSourceIdentity(photo, sourceIndex),
        source: String(photo.source || ''),
        source_pdf_doc_id: String(photo.sourcePdfDocId || '') || null,
        source_pdf_name: String(photo.sourcePdfName || '') || null,
        source_pdf_image_id: String(photo.sourcePdfImageId || '') || null,
        source_pdf_page: Number.isFinite(Number(photo.sourcePdfPage))
            ? Number(photo.sourcePdfPage)
            : null,
        reason_code: 'auxiliary-document-visual',
        };
    });
}

export function documentEvidenceDerivationFingerprint(
    photos: UnknownRecord[],
    rawPhotosValue: unknown = photos,
): string {
    return fingerprintValue(documentEvidenceExclusionReceipt(photos, rawPhotosValue));
}

function hasExactDocumentDerivationReceipt(
    receipt: UnknownRecord,
    allPhotos: UnknownRecord[],
    documentVisuals: UnknownRecord[],
): boolean {
    const rootFingerprint = String(receipt.source_fingerprint || '').trim();
    const legacyDerivationFingerprint = LEGACY_DOCUMENT_VISUAL_DERIVATION_FINGERPRINTS.get(rootFingerprint);
    if (legacyDerivationFingerprint
        && legacyDerivationFingerprint === documentEvidenceDerivationFingerprint(documentVisuals, allPhotos)) {
        return true;
    }

    if (!isExactCount(receipt.document_evidence_excluded_count, documentVisuals.length)) return false;
    const exclusions = Array.isArray(receipt.document_evidence_exclusions)
        ? receipt.document_evidence_exclusions
        : [];
    if (exclusions.length !== documentVisuals.length) return false;

    const expected = documentEvidenceExclusionReceipt(documentVisuals, allPhotos);
    return expected.every((row, index) => {
        const candidate = record(exclusions[index]);
        return !!candidate
            && candidate.reason_code === 'auxiliary-document-visual'
            && candidate.photo_id === row.photo_id
            && candidate.photo_name === row.photo_name
            && candidate.photo_index === row.photo_index
            && candidate.relative_path === row.relative_path
            && candidate.source === row.source
            && candidate.source_pdf_doc_id === row.source_pdf_doc_id
            && candidate.source_pdf_name === row.source_pdf_name
            && candidate.source_pdf_image_id === row.source_pdf_image_id
            && candidate.source_pdf_page === row.source_pdf_page;
    });
}

/**
 * Reinterpret the exact legacy 4/65 class of receipt without buying another
 * vision run. This is deliberately narrower than a generic partial-receipt
 * repair: every required customer photo must already have one identity-bound
 * observation, and every unreadable source must be a PDF-derived auxiliary.
 * A single unreadable genuine photo returns null and preserves the hard stop.
 */
export function healDocumentDerivedPhotoCoverage(
    value: unknown,
    photosValue: unknown,
): UnknownRecord | null {
    const receipt = record(value);
    const allPhotos = (Array.isArray(photosValue) ? photosValue : [])
        .map(record)
        .filter(Boolean) as UnknownRecord[];
    const documentVisuals = allPhotos.filter(isDocumentDerivedVisual);
    if (!receipt || documentVisuals.length === 0) return null;
    if (!hasExactPreHealLineage(receipt, allPhotos)) return null;
    if (!hasExactDocumentDerivationReceipt(receipt, allPhotos, documentVisuals)) return null;

    const allIds = allPhotos.map(stablePhotoId);
    if (allIds.some(id => !id) || new Set(allIds).size !== allIds.length) return null;

    const requiredPhotos = selectRequiredProjectPhotos(allPhotos);
    if (!hasExactLegacyPartialTuple(receipt, requiredPhotos.length, documentVisuals.length)) return null;
    const requiredIds = requiredPhotos.map(stablePhotoId);
    const allIdSet = new Set(allIds);
    const documentIds = new Set(documentVisuals.map(stablePhotoId));
    const unreadable = Array.isArray(receipt.unreadable_photos) ? receipt.unreadable_photos : [];
    const documentById = new Map(documentVisuals.map(photo => [stablePhotoId(photo), photo]));
    const unreadableIds = new Set<string>();
    for (const candidate of unreadable) {
        const entry = record(candidate);
        const id = String(entry?.photo_id || entry?.id || '').trim();
        const documentPhoto = documentById.get(id);
        if (!entry || !id || unreadableIds.has(id) || !documentIds.has(id) || !documentPhoto) return null;
        const sourceIndex = allPhotos.indexOf(documentPhoto);
        const identity = photoSourceIdentity(documentPhoto, sourceIndex);
        if (String(entry.photo_name || '') !== identity.photo_name
            || !isExactCount(entry.photo_index, sourceIndex)) {
            return null;
        }
        unreadableIds.add(id);
    }
    if (unreadableIds.size !== documentIds.size) return null;

    const observationsById = exactObservationMap(receipt);
    if (!observationsById) return null;
    if (observationsById.size !== requiredPhotos.length) return null;
    if ([...observationsById.keys()].some(id => !allIdSet.has(id))) return null;
    const healedObservations: UnknownRecord[] = [];
    for (let index = 0; index < requiredPhotos.length; index += 1) {
        const photo = requiredPhotos[index];
        const requiredId = requiredIds[index];
        if (!photo || !requiredId) return null;
        const observation = observationsById.get(requiredId);
        if (!observation) return null;
        const identity = photoSourceIdentity(photo, index);
        if (String(observation.photo_name || '') !== identity.photo_name) return null;
        healedObservations.push({
            ...observation,
            photo_index: index,
            photo_id: identity.photo_id,
            photo_name: identity.photo_name,
            source_id: `photo-${index + 1}`,
        });
    }

    // No provider failure involving an omitted required customer photo may be
    // hidden by this migration. Identity-complete required observations above
    // prove the positive side; this rejects any explicitly named failure too.
    const requiredIdSet = new Set(requiredIds);
    const hasCamelBatchErrors = Object.prototype.hasOwnProperty.call(receipt, 'batchErrors');
    const hasSnakeBatchErrors = Object.prototype.hasOwnProperty.call(receipt, 'batch_errors');
    if ((hasCamelBatchErrors && !Array.isArray(receipt.batchErrors))
        || (hasSnakeBatchErrors && !Array.isArray(receipt.batch_errors))) {
        return null;
    }
    // Both aliases have existed in persisted receipts. Inspect the union;
    // choosing one lets an empty preferred alias shadow a real failure in the
    // other representation.
    const batchErrors = [
        ...(Array.isArray(receipt.batchErrors) ? receipt.batchErrors : []),
        ...(Array.isArray(receipt.batch_errors) ? receipt.batch_errors : []),
    ];
    for (const error of batchErrors) {
        const errorPhotos = Array.isArray(record(error)?.photos) ? record(error)!.photos : [];
        // An opaque provider error cannot be proven to concern only excluded
        // document visuals. Fail closed and let the required-photo path rerun.
        if (errorPhotos.length === 0) return null;
        const errorPhotoIds: string[] = errorPhotos.map((photo: unknown) => String(record(photo)?.photo_id || '').trim());
        if (errorPhotoIds.some((id: string) => !id || requiredIdSet.has(id) || !documentIds.has(id))) return null;
    }

    const sourceFingerprint = photoForensicSourceFingerprint(requiredPhotos);
    const priorAttempt = record(receipt.analysis_attempt) || {};
    const healed: UnknownRecord = {
        ...receipt,
        photos_analysed: requiredPhotos.length,
        input_photos_count: requiredPhotos.length,
        selected_photos_count: requiredPhotos.length,
        submitted_photos_count: requiredPhotos.length,
        coverage_status: 'complete',
        coverage: {
            status: 'complete',
            expected: requiredPhotos.length,
            submitted: requiredPhotos.length,
            selected: requiredPhotos.length,
            analysed: requiredPhotos.length,
        },
        observations: healedObservations,
        unreadable_photo_count: 0,
        unreadable_photos: [],
        // This decision names the old unreadable set. Once that exact legacy
        // tuple is healed there is no unreadable set, so retaining the decision
        // would misdescribe the new receipt and its projected evidence.
        unreadable_acknowledgement: undefined,
        requires_review: false,
        review_reason: undefined,
        error: undefined,
        batchErrors: undefined,
        batch_errors: undefined,
        skipped: requiredPhotos.length === 0,
        skippedReason: requiredPhotos.length === 0 ? 'no required project photos supplied' : undefined,
        source_fingerprint: sourceFingerprint,
        source_fingerprint_version: PHOTO_SOURCE_FINGERPRINT_VERSION,
        analysis_attempt: {
            ...priorAttempt,
            source_count: requiredPhotos.length,
            source_fingerprint: sourceFingerprint,
            source_fingerprint_version: PHOTO_SOURCE_FINGERPRINT_VERSION,
        },
        document_evidence_excluded_count: documentVisuals.length,
        document_evidence_exclusions: documentEvidenceExclusionReceipt(documentVisuals, allPhotos),
    };
    return healed;
}
